import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Aviso,
  Botao,
  BotaoLink,
  Campo,
  CampoArea,
  Carregando,
  Cartao,
  Dado,
  Etiqueta,
  Icone,
  Select,
  Selo,
  useDialogo,
} from '../../componentes/ui/index.ts';
import {
  api,
  cpf,
  cpfValido,
  dataHora,
  ErroApi,
  mascararCpf,
  mascararTelefone,
  moeda,
} from '../../lib/api.ts';
import {
  ROTULO_PAGAMENTO,
  type FichaCompleta,
  type Lancamento,
  type RegistroAudit,
  type Saldo,
} from '../../lib/tipos.ts';

type Resposta = { inscricao: FichaCompleta; saldo: Saldo; extrato: Lancamento[] };

/**
 * Campos de saúde na ordem do formulário. `gatilho` marca a pergunta
 * sim/não que comanda os detalhes logo abaixo dela — aqui não escondemos
 * nada por causa disso (o admin precisa ver a ficha inteira, inclusive o
 * que ficou em branco), mas o rótulo deixa a dependência legível.
 */
const CAMPOS_SAUDE = [
  { chave: 'temPlano', rotulo: 'Tem plano de saúde?' },
  { chave: 'planoOperadora', rotulo: 'Plano — nome/operadora' },
  { chave: 'planoCarteirinha', rotulo: 'Plano — carteirinha' },
  { chave: 'temAlergia', rotulo: 'Tem alergia/restrição?' },
  { chave: 'alergiaDescricao', rotulo: 'Alergias e restrições' },
  { chave: 'temMedicacaoContinua', rotulo: 'Usa medicação contínua?' },
  { chave: 'medicacaoDescricao', rotulo: 'Medicação contínua' },
  { chave: 'cuidadosEspeciais', rotulo: 'Cuidados especiais' },
];

const SEXOS = [
  { valor: 'feminino', rotulo: 'Feminino' },
  { valor: 'masculino', rotulo: 'Masculino' },
];

const SIM_NAO = [
  { valor: 'sim', rotulo: 'Sim' },
  { valor: 'nao', rotulo: 'Não' },
];

const TAMANHOS = ['PP', 'P', 'M', 'G', 'GG', 'XG'].map((t) => ({ valor: t, rotulo: t }));

/**
 * `dados.nascimento` é "AAAA-MM-DD" puro, sem hora — passar isso por
 * `new Date()` conta como UTC meia-noite, e em fuso negativo (Brasil) o
 * `toLocaleDateString` local mostra o dia anterior. Reformata na unha, sem
 * criar `Date` nenhum.
 */
function dataSimples(v: string | undefined): string {
  const m = v?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
}

/**
 * Edição total pelo admin, para corrigir erro de digitação sem obrigar o
 * responsável a refazer a ficha. Toda alteração vira `inscricao.editada` no
 * audit log com o diff — é o que substitui a trilha em papel.
 */
export function Ficha() {
  const { id = '' } = useParams();
  const [dados, setDados] = useState<Resposta | null>(null);
  const [historico, setHistorico] = useState<RegistroAudit[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [editando, setEditando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  const { perguntar, dialogo } = useDialogo();

  const [form, setForm] = useState({
    nomeAdolescente: '',
    nomeResponsavel: '',
    email: '',
    telefoneResponsavel: '',
    telefoneAdolescente: '',
    house: '',
    pedidoQuarto: '',
    valorTotal: '',
    // Mantém as chaves que a ficha já tinha e não têm campo próprio aqui
    // (ex.: `autorizo`, gravado no envio público) — o PATCH substitui o
    // objeto inteiro, então perder uma chave aqui apaga um dado de verdade.
    dados: {} as Record<string, string>,
    saude: {} as Record<string, string>,
  });

  const carregar = useCallback(async () => {
    try {
      const r = await api<Resposta>(`/api/admin/inscricoes/${id}`);
      setDados(r);
      setForm({
        nomeAdolescente: r.inscricao.nomeAdolescente,
        nomeResponsavel: r.inscricao.nomeResponsavel ?? '',
        email: r.inscricao.email,
        telefoneResponsavel: mascararTelefone(r.inscricao.telefoneResponsavel ?? ''),
        telefoneAdolescente: mascararTelefone(r.inscricao.telefoneAdolescente ?? ''),
        house: r.inscricao.house ?? '',
        pedidoQuarto: r.inscricao.pedidoQuarto ?? '',
        valorTotal: r.inscricao.valorTotal,
        dados: (r.inscricao.dados as Record<string, string>) ?? {},
        saude: (r.inscricao.saude as Record<string, string>) ?? {},
      });

      const h = await api<{ registros: RegistroAudit[] }>(
        `/api/admin/audit?entity=inscricoes&entityId=${id}&limite=20`,
      );
      setHistorico(h.registros);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Erro ao carregar');
    } finally {
      setCarregando(false);
    }
  }, [id]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function salvar() {
    setSalvando(true);
    setErro(null);
    try {
      await api(`/api/admin/inscricoes/${id}`, {
        metodo: 'PATCH',
        corpo: {
          ...form,
          telefoneResponsavel: form.telefoneResponsavel.replace(/\D/g, ''),
          telefoneAdolescente: form.telefoneAdolescente.replace(/\D/g, '') || null,
          house: form.house || null,
        },
      });
      setEditando(false);
      setSucesso('Alterações salvas e registradas no histórico.');
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  }

  async function acao(caminho: string) {
    try {
      await api(`/api/admin/inscricoes/${id}/${caminho}`, { metodo: 'POST', corpo: {} });
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Erro na operação');
    }
  }

  function definirDados(chave: string, valor: string) {
    setForm((f) => ({ ...f, dados: { ...f.dados, [chave]: valor } }));
  }

  async function marcarAutorizacao(assinada: boolean) {
    try {
      await api(`/api/admin/inscricoes/${id}`, {
        metodo: 'PATCH',
        corpo: { autorizacaoAssinada: assinada },
      });
      await carregar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Erro ao atualizar');
    }
  }

  if (carregando) return <Carregando />;
  if (!dados) return <Aviso tipo="erro">{erro ?? 'Ficha não encontrada.'}</Aviso>;

  const { inscricao, saldo, extrato } = dados;
  /** Atalho para o jsonb da ficha, que é lido em vários lugares abaixo. */
  const d = inscricao.dados as Record<string, string> | undefined;

  return (
    <div className="pilha-6">
      {dialogo}

      <BotaoLink
        para="/painel/inscricoes"
        variante="fantasma"
        tamanho="pequeno"
        icone={<Icone.Voltar tamanho={15} />}
      >
        Voltar às inscrições
      </BotaoLink>

      <div className="linha linha-entre linha-topo">
        <div className="pilha-2">
          <Etiqueta>{inscricao.protocolo}</Etiqueta>
          <h2>{inscricao.nomeAdolescente}</h2>
          <div className="texto-suave texto-sm">
            {inscricao.status === 'submitted' ? (
              `Enviada em ${dataHora(inscricao.enviadaEm)}`
            ) : inscricao.status === 'draft' ? (
              <Selo tom="alerta">rascunho — ainda não enviada</Selo>
            ) : (
              <Selo tom="erro">cancelada em {dataHora(inscricao.canceladaEm)}</Selo>
            )}
          </div>
        </div>

        <div style={{ textAlign: 'right' }}>
          <div className="texto-fraco">Saldo</div>
          <div
            className="numeral mono"
            style={{ color: saldo.quitado ? 'var(--sucesso)' : 'var(--laranja-texto)' }}
          >
            {saldo.quitado ? 'quitado' : moeda(saldo.saldo)}
          </div>
        </div>
      </div>

      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {sucesso && <Aviso tipo="sucesso">{sucesso}</Aviso>}

      {/*
        Duas fichas, de propósito: o adolescente é quem participa do
        acampamento, o responsável é só o contato de quem cuida dele. Antes
        os dados do próprio adolescente (nascimento, camiseta, escola,
        contato de emergência) nem apareciam aqui — só o contato do
        responsável era visível e editável. Um único `editando` controla as
        duas, para não virar duas sessões de edição independentes.
      */}
      <Cartao
        marcado
        titulo="Adolescente"
        acao={
          !editando ? (
            <Botao
              variante="secundario"
              tamanho="pequeno"
              onClick={() => setEditando(true)}
              icone={<Icone.Editar tamanho={14} />}
            >
              Editar
            </Botao>
          ) : (
            <div className="linha" style={{ gap: 'var(--gap-2)' }}>
              <Botao
                variante="fantasma"
                tamanho="pequeno"
                onClick={() => {
                  setEditando(false);
                  void carregar();
                }}
              >
                Cancelar
              </Botao>
              <Botao tamanho="pequeno" onClick={() => void salvar()} carregando={salvando}>
                Salvar
              </Botao>
            </div>
          )
        }
      >
        {editando ? (
          <div className="pilha">
            <Aviso tipo="info">
              Toda alteração fica registrada no histórico com o que mudou, quem mudou e quando.
            </Aviso>

            <div className="grade">
              <Campo
                rotulo="Nome do adolescente"
                value={form.nomeAdolescente}
                onChange={(e) => setForm({ ...form, nomeAdolescente: e.target.value })}
              />
              <Campo
                rotulo="Data de nascimento"
                type="date"
                value={form.dados.nascimento ?? ''}
                onChange={(e) => definirDados('nascimento', e.target.value)}
              />
              <Select
                rotulo="Sexo"
                opcoes={SEXOS}
                valor={form.dados.sexo ?? null}
                aoMudar={(v) => definirDados('sexo', v)}
                placeholder="Selecione"
              />
              <Campo
                rotulo="WhatsApp do adolescente"
                value={form.telefoneAdolescente}
                onChange={(e) =>
                  setForm({ ...form, telefoneAdolescente: mascararTelefone(e.target.value) })
                }
                inputMode="numeric"
              />
              <Campo
                rotulo="CPF do adolescente"
                value={form.dados.cpfAdolescente ?? ''}
                onChange={(e) => definirDados('cpfAdolescente', mascararCpf(e.target.value))}
                inputMode="numeric"
                erro={
                  form.dados.cpfAdolescente && !cpfValido(form.dados.cpfAdolescente)
                    ? 'CPF inválido'
                    : null
                }
              />
              <Campo
                rotulo="RG ou CIN do adolescente"
                value={form.dados.rgAdolescente ?? ''}
                onChange={(e) => definirDados('rgAdolescente', e.target.value)}
              />
              <Campo
                rotulo="House"
                value={form.house}
                onChange={(e) => setForm({ ...form, house: e.target.value })}
              />
              <Select
                rotulo="Divide quarto/mala?"
                opcoes={SIM_NAO}
                valor={form.dados.divideQuarto ?? null}
                aoMudar={(v) => definirDados('divideQuarto', v)}
                placeholder="Selecione"
              />
              <Campo
                rotulo="Divide com"
                value={form.dados.divideCom ?? ''}
                onChange={(e) => definirDados('divideCom', e.target.value)}
                opcional
              />
              <Campo
                rotulo="Parentesco/motivo da divisão"
                value={form.dados.divideMotivo ?? ''}
                onChange={(e) => definirDados('divideMotivo', e.target.value)}
                opcional
              />
              <Select
                rotulo="Tamanho da camiseta"
                opcoes={TAMANHOS}
                valor={form.dados.camiseta ?? null}
                aoMudar={(v) => definirDados('camiseta', v)}
                placeholder="Selecione"
                opcional
              />
              <Campo
                rotulo="Valor total"
                value={form.valorTotal}
                onChange={(e) => setForm({ ...form, valorTotal: e.target.value })}
                inputMode="decimal"
                prefixo="R$"
              />
            </div>

            <h4 style={{ marginTop: 'var(--gap-3)' }}>Saúde</h4>
            {CAMPOS_SAUDE.map((c) => (
              <CampoArea
                key={c.chave}
                rotulo={c.rotulo}
                value={form.saude[c.chave] ?? ''}
                onChange={(e) =>
                  setForm({ ...form, saude: { ...form.saude, [c.chave]: e.target.value } })
                }
              />
            ))}
          </div>
        ) : (
          <div className="pilha-3">
            <Dado titulo="Nascimento">{dataSimples(d?.nascimento)}</Dado>
            <Dado titulo="Sexo">{d?.sexo}</Dado>
            <Dado titulo="WhatsApp">{form.telefoneAdolescente}</Dado>
            <Dado titulo="CPF">{cpf(d?.cpfAdolescente)}</Dado>
            <Dado titulo="RG/CIN">{d?.rgAdolescente}</Dado>
            <Dado titulo="House">{inscricao.house}</Dado>
            <Dado titulo="Divide quarto/mala">
              {d?.divideQuarto === 'sim' ? `sim — ${d?.divideCom ?? ''}` : d?.divideQuarto}
            </Dado>
            {d?.divideQuarto === 'sim' && (
              <Dado titulo="Parentesco/motivo">{d?.divideMotivo}</Dado>
            )}
            <Dado titulo="Camiseta">{d?.camiseta}</Dado>
            {/* Fichas anteriores à pergunta de divisão guardavam o pedido
                em texto livre; só aparece quando existe. */}
            {inscricao.pedidoQuarto && (
              <Dado titulo="Pedido de quarto">{inscricao.pedidoQuarto}</Dado>
            )}
            <Dado titulo="Valor total">{moeda(inscricao.valorTotal)}</Dado>

            <h4 style={{ marginTop: 'var(--gap-3)' }}>Saúde</h4>
            {inscricao.saude && Object.keys(inscricao.saude).length > 0 ? (
              CAMPOS_SAUDE.map((c) => (
                <Dado key={c.chave} titulo={c.rotulo}>
                  {String((inscricao.saude as Record<string, string>)[c.chave] ?? '')}
                </Dado>
              ))
            ) : (
              <Aviso tipo="alerta">
                Sem informações de saúde. Não dá para viajar sem isso — vale acionar o responsável.
              </Aviso>
            )}
          </div>
        )}
      </Cartao>

      {/* Secundário de propósito: é só contato, não o foco da ficha. */}
      <Cartao titulo="Responsável">
        {editando ? (
          <div className="grade">
            <Campo
              rotulo="Nome do responsável"
              value={form.nomeResponsavel}
              onChange={(e) => setForm({ ...form, nomeResponsavel: e.target.value })}
            />
            <Campo
              rotulo="Grau de parentesco"
              value={form.dados.parentesco ?? ''}
              onChange={(e) => definirDados('parentesco', e.target.value)}
            />
            <Campo
              rotulo="CPF do responsável"
              value={form.dados.cpfResponsavel ?? ''}
              onChange={(e) => definirDados('cpfResponsavel', mascararCpf(e.target.value))}
              inputMode="numeric"
              erro={
                form.dados.cpfResponsavel && !cpfValido(form.dados.cpfResponsavel)
                  ? 'CPF inválido'
                  : null
              }
            />
            <Campo
              rotulo="E-mail"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <Campo
              rotulo="Telefone do responsável"
              value={form.telefoneResponsavel}
              onChange={(e) =>
                setForm({ ...form, telefoneResponsavel: mascararTelefone(e.target.value) })
              }
              inputMode="numeric"
            />
          </div>
        ) : (
          <div className="pilha-3">
            <Dado titulo="Nome">{inscricao.nomeResponsavel}</Dado>
            <Dado titulo="Parentesco">{d?.parentesco}</Dado>
            <Dado titulo="CPF">{cpf(d?.cpfResponsavel)}</Dado>
            <Dado titulo="E-mail">{inscricao.email}</Dado>
            <Dado titulo="Telefone">{form.telefoneResponsavel}</Dado>
          </div>
        )}
      </Cartao>

      <Cartao titulo="Situação">
        <div className="pilha">
          <Botao
            variante={inscricao.autorizacaoAssinadaEm ? 'secundario' : 'primario'}
            tamanho="pequeno"
            onClick={() => void marcarAutorizacao(!inscricao.autorizacaoAssinadaEm)}
            icone={<Icone.Check tamanho={14} />}
          >
            {inscricao.autorizacaoAssinadaEm
              ? `Assinada em ${dataHora(inscricao.autorizacaoAssinadaEm)} — desmarcar`
              : 'Marcar autorização como assinada'}
          </Botao>

          <div className="linha">
            {inscricao.status === 'submitted' && (
              <Botao
                variante="vazado"
                tamanho="pequeno"
                onClick={() =>
                  perguntar({
                    titulo: 'Reabrir a ficha?',
                    texto:
                      'A ficha volta a ser rascunho e o responsável recupera o controle para corrigir. Diferente de editar aqui, que mantém a ficha enviada.',
                    confirmar: 'Reabrir',
                    aoConfirmar: () => acao('reabrir'),
                  })
                }
              >
                Reabrir para o responsável
              </Botao>
            )}

            {inscricao.status !== 'cancelled' && (
              <Botao
                variante="perigo"
                tamanho="pequeno"
                onClick={() =>
                  perguntar({
                    titulo: 'Cancelar esta inscrição?',
                    texto:
                      'O e-mail e o nome ficam livres para uma nova inscrição. Nada é apagado — a ficha só muda de situação.',
                    confirmar: 'Cancelar inscrição',
                    perigo: true,
                    aoConfirmar: () => acao('cancelar'),
                  })
                }
              >
                Cancelar inscrição
              </Botao>
            )}
          </div>
        </div>
      </Cartao>

      {extrato.length > 0 && (
        <Cartao titulo="Pagamentos">
          <div className="tabela-caixa">
            <table className="tabela">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Forma</th>
                  <th className="num">Valor</th>
                </tr>
              </thead>
              <tbody>
                {extrato.map((l) => (
                  <tr key={l.id}>
                    <td className="mono">{dataHora(l.pagoEm)}</td>
                    <td>
                      <span className="linha" style={{ gap: 'var(--gap-2)' }}>
                        {ROTULO_PAGAMENTO[l.tipo]}
                        {l.natureza === 'estorno' && <Selo tom="erro">estorno</Selo>}
                      </span>
                    </td>
                    <td
                      className="num mono"
                      style={l.natureza === 'estorno' ? { color: 'var(--erro)' } : undefined}
                    >
                      {l.natureza === 'estorno' ? '−' : ''}
                      {moeda(l.valor)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Cartao>
      )}

      <Cartao titulo="Histórico">
        {historico.length === 0 ? (
          <p className="texto-fraco">Nenhum registro.</p>
        ) : (
          <div className="pilha-3">
            {historico.map((r) => (
              <div
                key={r.id}
                style={{
                  borderLeft: '2px solid var(--borda-media)',
                  paddingLeft: 'var(--gap-4)',
                }}
              >
                <div className="linha" style={{ gap: 'var(--gap-2)' }}>
                  <Selo tom={r.action.includes('cancel') ? 'erro' : 'neutro'}>{r.action}</Selo>
                  <span className="texto-fraco">{dataHora(r.criadoEm)}</span>
                </div>
                <div className="texto-fraco texto-sm" style={{ marginTop: 'var(--gap-1)' }}>
                  {r.actorType === 'system'
                    ? 'Sistema'
                    : r.actorType === 'guardian'
                      ? 'Responsável'
                      : (r.atorNome ?? 'Conta removida')}
                  {r.atorEmail && ` · ${r.atorEmail}`}
                </div>
                {r.payload && Object.keys(r.payload).length > 0 && (
                  <pre
                    className="mono texto-fraco"
                    style={{
                      marginTop: 'var(--gap-2)',
                      fontSize: 'var(--texto-xs)',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      background: 'var(--superficie-alta)',
                      padding: 'var(--gap-3)',
                      borderRadius: 'var(--raio-sm)',
                    }}
                  >
                    {JSON.stringify(r.payload, null, 2)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}
      </Cartao>
    </div>
  );
}
